import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { SignIn, ShieldCheck, Boat, ClockCountdown, EnvelopeSimple } from "@phosphor-icons/react";
import { usePortalAuth } from "@/contexts/PortalAuthContext";
import { formatApiError } from "@/lib/api";
import { BRAND, PortalLogo } from "./portalBrand";

const POINTS = [
  { icon: Boat, text: "Track every shipment in real time" },
  { icon: ClockCountdown, text: "Live ETAs and voyage progress" },
  { icon: ShieldCheck, text: "Your documents, secure and in one place" },
];

export default function PortalLogin() {
  const { login } = usePortalAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate("/portal");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(135deg,#1e3a8a 0%,#1d4ed8 55%,#2563eb 100%)" }}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 0, transparent 40%), radial-gradient(circle at 80% 70%, #fff 0, transparent 35%)" }} />
        <div className="relative">
          <div className="bg-white rounded-lg inline-flex px-4 py-3 shadow-lg"><PortalLogo className="h-10 w-auto object-contain" /></div>
          <h1 className="mt-10 text-3xl font-bold leading-tight">Your shipments,<br />tracked end to end.</h1>
          <p className="mt-3 text-blue-100 max-w-sm">{BRAND.tagline}</p>
          <ul className="mt-8 space-y-3">
            {POINTS.map((p, i) => (
              <li key={i} className="flex items-center gap-3 text-blue-50">
                <span className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center"><p.icon size={18} weight="bold" /></span>
                {p.text}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative text-[12px] text-blue-200">Need help? <a href={`mailto:${BRAND.supportEmail}`} className="underline hover:text-white">{BRAND.supportEmail}</a></div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex justify-center mb-6"><PortalLogo className="h-10 w-auto object-contain" fallbackClass="text-xl" /></div>
          <h2 className="text-2xl font-bold text-slate-900">Welcome back</h2>
          <p className="text-sm text-slate-500 mt-1">Sign in to track your shipments.</p>
          <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="portal-email"
                className="mt-1.5 w-full h-11 px-3 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="portal-password"
                className="mt-1.5 w-full h-11 px-3 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" placeholder="••••••••" />
            </div>
            <div className="flex justify-end -mt-1">
              <Link to="/portal/forgot-password" data-testid="portal-forgot-link"
                className="text-[12px] text-blue-700 hover:underline">Forgot password?</Link>
            </div>
            <button type="submit" disabled={busy} data-testid="portal-login-btn"
              className="w-full h-11 rounded-md bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
              <SignIn size={16} /> {busy ? "Signing in…" : "Sign in"}
            </button>
            <div className="pt-3 border-t border-slate-100 text-center">
              <p className="text-[12px] text-slate-500 inline-flex items-center justify-center gap-1.5">
                <EnvelopeSimple size={13} /> A {BRAND.name} customer without a login?
              </p>
              <Link to="/portal/request-access" data-testid="portal-request-access-link"
                className="mt-1 inline-block text-[13px] font-semibold text-blue-700 hover:underline">Request access</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
